import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API, useBranding } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Loader2, CheckCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { useTheme } from '@/components/ThemeProvider';
import CaptchaWidget from '@/components/CaptchaWidget';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithToken, verifyTOTP } = useAuth();
  const { brand_name, logo_data, logo_dark_data, logo_light_data } = useBranding();
  const { theme } = useTheme();
  const activeLogo = (theme === 'dark' ? logo_dark_data : logo_light_data) || logo_data;
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const impersonateToken = searchParams.get('impersonate_token');

  // 2FA step state
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  // Unverified account state
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // CAPTCHA state
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const captchaRequired = captchaConfig?.captcha_enabled && captchaConfig?.merchant_login_captcha;
  const captchaType = captchaConfig?.captcha_type || 'math';
  const captchaPassed = !captchaRequired || (captchaType === 'math' ? captchaVerified : !!captchaToken);

  useEffect(() => {
    axios.get(`${API}/auth/captcha-config`)
      .then(r => setCaptchaConfig(r.data))
      .catch(() => {});
  }, []);

  const handleCaptchaVerified = useCallback((v) => setCaptchaVerified(v), []);
  const handleCaptchaToken = useCallback((t) => setCaptchaToken(t), []);

  useEffect(() => {
    document.title = 'Login | CoinPayUp';

    // Handle impersonate (backdoor login) token
    if (impersonateToken) {
      setLoading(true);
      loginWithToken(impersonateToken)
        .then((userData) => {
          toast.success(`Logged in as ${userData.email}`);
          navigate(userData.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
        })
        .catch(() => {
          toast.error('Impersonate token is invalid or expired.');
          setLoading(false);
        });
    }
  }, [impersonateToken, loginWithToken, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!captchaPassed) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    setUnverifiedEmail('');
    setResendSent(false);
    setLoading(true);
    try {
      const payload = {
        ...loginForm,
        ...(captchaRequired && captchaType !== 'math' ? { captcha_token: captchaToken } : {}),
      };
      const result = await login(payload.email, payload.password, payload.captcha_token);
      // 2FA required
      if (result?.requires_2fa) {
        setTempToken(result.temp_token);
        setTwoFAStep(true);
        return;
      }
      toast.success('Login successful!');
      navigate(result.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(loginForm.email);
      } else if (detail === 'CAPTCHA_FAILED') {
        toast.error('CAPTCHA verification failed. Please try again.');
      } else {
        toast.error(detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const userData = await verifyTOTP(tempToken, totpCode);
      toast.success('Login successful!');
      navigate(userData.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid code. Please try again.';
      toast.error(detail);
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: unverifiedEmail || loginForm.email });
      setResendSent(true);
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="login-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />

      <div className="w-full max-w-md relative z-10">

        {/* Impersonate loading state */}
        {impersonateToken && loading && (
          <div className="text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="text-sm text-muted-foreground">Logging in as merchant...</p>
          </div>
        )}

        {!impersonateToken && (
          <>
            {/* 2FA Step */}
            {twoFAStep ? (
              <>
                <button onClick={() => { setTwoFAStep(false); setTempToken(''); setTotpCode(''); }} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors text-sm">
                  <ArrowLeft className="h-4 w-4" /> Back to login
                </button>
                <div className="flex items-center gap-2 mb-8">
                  <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-bold text-2xl">{brand_name || 'CoinPayUp'}</span>
                </div>
                <Card className="border-border bg-card" data-testid="totp-step-card">
                  <CardHeader>
                    <CardTitle>Two-Factor Authentication</CardTitle>
                    <CardDescription>Enter the 6-digit code from your authenticator app (Google Authenticator / Authy)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handle2FASubmit} className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="totp-code">Authenticator Code</Label>
                        <Input
                          id="totp-code"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          placeholder="000000"
                          value={totpCode}
                          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="text-center text-2xl tracking-[0.5em] font-mono"
                          autoFocus
                          required
                          data-testid="totp-code-input"
                        />
                        <p className="text-xs text-muted-foreground text-center">Code refreshes every 30 seconds</p>
                      </div>
                      <Button type="submit" className="w-full" disabled={loading || totpCode.length !== 6} data-testid="totp-submit-btn">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                        Verify & Sign In
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </>
            ) : (
            <>
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>

            <div className="flex items-center gap-2 mb-8">
              {activeLogo ? (
                <img src={activeLogo} alt={brand_name} className="h-9 max-w-[140px] object-contain" />
              ) : (
                <>
                  <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                    <span className="text-white font-bold text-xl">C</span>
                  </div>
                  <span className="font-bold text-2xl">{brand_name || 'CoinPayUp'}</span>
                </>
              )}
            </div>

            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Welcome Back</CardTitle>
                <CardDescription>Sign in to your merchant account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@business.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                        Forgot password?
                      </Link>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                    />
                  </div>

                  {/* Unverified email alert */}
                  {unverifiedEmail && (
                    <div className="p-3.5 rounded-xl bg-amber-500/8 border border-amber-500/25 space-y-2" data-testid="unverified-email-alert">
                      <p className="text-sm text-amber-300 font-medium">Email not verified</p>
                      <p className="text-xs text-amber-300/70">
                        Please verify your email address before logging in. Check your inbox for the verification link.
                      </p>
                      {resendSent ? (
                        <div className="flex items-center gap-1.5 text-xs text-emerald-400" data-testid="resend-sent-confirm">
                          <CheckCircle className="h-3.5 w-3.5" /> New verification email sent!
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={resendLoading}
                          className="text-xs text-amber-300 underline hover:text-amber-200 flex items-center gap-1"
                          data-testid="login-resend-verification-btn"
                        >
                          {resendLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          Resend verification email
                        </button>
                      )}
                    </div>
                  )}

                  {/* CAPTCHA */}
                  {captchaRequired && (
                    <CaptchaWidget
                      type={captchaType}
                      siteKey={captchaConfig?.[`${captchaType}_site_key`] || ''}
                      onVerified={handleCaptchaVerified}
                      onToken={handleCaptchaToken}
                    />
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-primary text-white"
                    disabled={loading || !captchaPassed}
                    data-testid="login-submit-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <p className="text-center text-sm text-muted-foreground mt-5">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline" data-testid="go-to-register-link">Create one</Link>
            </p>
            </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
